import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  sensorLabelSourceId,
  type SensorLabelSourceId,
} from './SensorLabelSource.js'

/** Identifier type for public.sensor_display_ */
export type SensorDisplaySensorSn = string & {
  __brand: 'SensorDisplaySensorSn'
}

/** Represents the table public.sensor_display_ */
export default interface SensorDisplayTable {
  /** Database type: pg_catalog.text */
  sensorSn: ColumnType<
    SensorDisplaySensorSn,
    SensorDisplaySensorSn,
    SensorDisplaySensorSn
  >

  /** Database type: pg_catalog.text */
  loggerSn: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  label: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  labelSource: ColumnType<
    SensorLabelSourceId | null,
    SensorLabelSourceId | null,
    SensorLabelSourceId | null
  >

  /** Database type: pg_catalog.float8 */
  depthM: ColumnType<number | null, number | null, number | null>

  /**
   * Database type: pg_catalog.text
   * UNUSED since 2026-10-27 (sensor_site_label_): a label typed by a project admin belongs to a site, not to the sensor_sn, so it moved to sensor_site_label_. Nothing reads or writes this column; the deploy migration role cannot remove columns, so it stays until an owner-applied migration removes it (#4749).
   */
  operatorLabel: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>
}

export type SensorDisplay = Selectable<SensorDisplayTable>

export type SensorDisplayInitializer = Insertable<SensorDisplayTable>

export type SensorDisplayMutator = Updateable<SensorDisplayTable>

export const sensorDisplaySensorSn =
  z.string() as unknown as z.Schema<SensorDisplaySensorSn>

export const sensorDisplay = z.object({
  sensorSn: sensorDisplaySensorSn,
  loggerSn: z.string(),
  label: z.string().nullable().optional(),
  labelSource: sensorLabelSourceId.nullable().optional(),
  depthM: z.number().nullable().optional(),
  operatorLabel: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
}) as unknown as z.Schema<SensorDisplay>

export const sensorDisplayInitializer = z.object({
  sensorSn: sensorDisplaySensorSn,
  loggerSn: z.string(),
  label: z.string().optional().nullable(),
  labelSource: sensorLabelSourceId.optional().nullable(),
  depthM: z.number().optional().nullable(),
  operatorLabel: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}) as unknown as z.Schema<SensorDisplayInitializer>

export const sensorDisplayMutator = z.object({
  sensorSn: sensorDisplaySensorSn.optional(),
  loggerSn: z.string().optional(),
  label: z.string().optional().nullable(),
  labelSource: sensorLabelSourceId.optional().nullable(),
  depthM: z.number().optional().nullable(),
  operatorLabel: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}) as unknown as z.Schema<SensorDisplayMutator>
